/**
 * =============================================================================
 *  BANG THUOC TINH - MOT BANG DUY NHAT CHO CA HAI DUONG SOAN CHU
 * =============================================================================
 *
 *  Nguoi dung bao: o go sua chu hien qua nhieu thong tin, lap lai voi bang Thong
 *  tin. Nen TAT CA dieu khien kieu chu - phong chu, co chu, dam / nghieng, can
 *  le, mau chu - da don het vao day, va bang nay hien TRONG lop noi Thong tin
 *  (InspectorPopover). O go chu ben duoi chi con o nhap va nut Áp dụng / Hủy.
 *
 *  MOT BANG cho ca ba ngu canh:
 *    - dang sua chu SAN CO trong tai lieu (co ghi chu chay thu + canh bao thay
 *      font, cam vao qua `style.note`);
 *    - dang go mot HOP CHU / GHI CHU moi hoac chon mot doi tuong chu (co them mau
 *      chu, mau nen, do mo, nut xoa);
 *    - dang chon mot HINH KHOI (chi co mau nen, do mo, nut xoa - khong co kieu chu).
 *
 *  Cai gi hai duong khac nhau THAT duoc the hien bang cac PHAN duoc cam vao
 *  (`style.note`, `fill`, `destructive`), khong bang mot bang thu hai. Do la
 *  cach hai duong da tung troi khoi nhau, va la thu bang nay duoc dung ra de tranh.
 */
import type { ReactNode } from 'react';
import {
  AlignToggles,
  DestructiveAction,
  FontFamilySelect,
  FontSizeCombo,
  StyleToggles,
} from './TextStyleControls';
import type { TextAlign } from './textEditCore';
import type { FontFamilyId } from '../core/fonts/families';

export interface StyleControls {
  fontFamily: { value: string | undefined; onChange: (id: FontFamilyId) => void };
  fontSize: { value: number; onChange: (pt: number) => void };
  bold: boolean;
  italic: boolean;
  onBold: (next: boolean) => void;
  onItalic: (next: boolean) => void;
  boldTitle?: string;
  italicTitle?: string;
  /** null = CHUA CHON GI (chu san co: de loi suy ra tu chu cu). */
  align: TextAlign | null;
  onAlign: (align: TextAlign) => void;
  alignSupported: boolean;
  alignDisabledTitle?: string;
  /** Mau chu ghi vao tep. Bo qua voi chu san co (giu mau tai lieu). */
  color?: { hex: string; onChange: (hex: string) => void };
  /** Ghi chu ngay duoi thanh kieu chu: canh bao doi font, ket qua chay thu... */
  note?: ReactNode;
}

export interface FillControl {
  hex: string;
  /** false = dang KHONG to (nhin xuyen qua). Khac "chua chon" - xem hop dong kieu. */
  enabled: boolean;
  onChange: (hex: string) => void;
  onToggle: () => void;
}

export interface PropertiesPanelProps {
  /** Tieu de ngan: "Sửa chữ", "Hộp chữ", "Khung chữ nhật"... */
  title: string;
  /** Bo dieu khien kieu chu. Bo qua = doi tuong khong co chu (hinh khoi). */
  style?: StyleControls;
  /** Mau nen hop. Bo qua voi duong sua chu san co. */
  fill?: FillControl;
  /** Do mo cua ca doi tuong, 0..1. */
  opacity?: { value: number; onChange: (v: number) => void };
  /** Nut xoa / che. */
  destructive?: { label: string; title: string; onClick: () => void; disabled?: boolean };
}

export function PropertiesPanel({ title, style, fill, opacity, destructive }: PropertiesPanelProps) {
  return (
    <section className="propertiesPanel" aria-label="Thuộc tính">
      <div className="propertiesTitle">{title}</div>

      {style && (
        <>
          <div className="editorStyleBar">
            <div className="editorStyleGroup">
              <span className="scopeLabel">Phông chữ</span>
              <FontFamilySelect value={style.fontFamily.value} onChange={style.fontFamily.onChange} />
            </div>
            <div className="editorStyleGroup">
              <span className="scopeLabel">Cỡ chữ</span>
              <FontSizeCombo value={style.fontSize.value} onCommit={style.fontSize.onChange} />
            </div>
            <div className="editorStyleGroup">
              <span className="scopeLabel">Kiểu chữ</span>
              <StyleToggles
                bold={style.bold}
                italic={style.italic}
                onBold={style.onBold}
                onItalic={style.onItalic}
                {...(style.boldTitle === undefined ? {} : { boldTitle: style.boldTitle })}
                {...(style.italicTitle === undefined ? {} : { italicTitle: style.italicTitle })}
              />
            </div>
            <div className="editorStyleGroup">
              <span className="scopeLabel">Căn lề</span>
              <AlignToggles
                active={style.align}
                onChange={style.onAlign}
                disabled={!style.alignSupported}
                {...(style.alignDisabledTitle === undefined
                  ? {}
                  : { disabledTitle: style.alignDisabledTitle })}
              />
            </div>
            {style.color && (
              <label className="editorColor" title="Màu chữ — màu này được ghi vào tệp">
                <span className="scopeLabel">Màu chữ</span>
                <input
                  type="color"
                  value={style.color.hex}
                  aria-label="Màu chữ"
                  onChange={(e) => style.color?.onChange(e.target.value)}
                />
              </label>
            )}
          </div>
          {style.note}
        </>
      )}

      {(fill || opacity) && (
        <div className="propertiesObjectRow">
          {fill && (
            <label className="propColor" title="Màu nền của đối tượng. Bỏ tô thì thấy nội dung bên dưới.">
              <span className="propColorLabel">Nền</span>
              <input
                type="color"
                value={fill.hex}
                disabled={!fill.enabled}
                aria-label="Màu nền"
                onChange={(e) => fill.onChange(e.target.value)}
              />
              <button
                type="button"
                className={`propToggle small${fill.enabled ? '' : ' active'}`}
                aria-pressed={!fill.enabled}
                title={
                  fill.enabled ? 'Bỏ tô nền' : 'Đang không tô nền — nội dung bên dưới nhìn xuyên qua'
                }
                onClick={fill.onToggle}
              >
                ⌀
              </button>
            </label>
          )}
          {opacity && (
            <label className="propOpacity" title="Độ mờ của cả đối tượng">
              <span className="propColorLabel">Mờ</span>
              <input
                type="range"
                min={5}
                max={100}
                step={5}
                value={Math.round(opacity.value * 100)}
                aria-label="Độ mờ"
                onChange={(e) => opacity.onChange(Number(e.target.value) / 100)}
              />
              <span className="propValue">{Math.round(opacity.value * 100)}%</span>
            </label>
          )}
        </div>
      )}

      {destructive && (
        <div className="propertiesActions">
          <DestructiveAction
            label={destructive.label}
            title={destructive.title}
            onClick={destructive.onClick}
            disabled={destructive.disabled === true}
          />
        </div>
      )}
    </section>
  );
}
